import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import classes from "./PlayerPointsModal2.module.css";
import { getPlayerPointsApi } from "../../http/http";
import { togglePlayerPointsModal } from "../../store/slices/playerPointsModalSlice";
import SmallLoader from "../SmallLoader";
import { useTranslation } from "react-i18next";

const STAT_ROWS = [
    { label: "Minutes Played", calcKey: "minutes", pointKey: "minutesPoints" },
    { label: "Player Rating", calcKey: "rating", pointKey: "ratingPoints" },
    { label: "Shots on Goal", calcKey: "shots", pointKey: "shotsPoints" },
    { label: "Goals", calcKey: "goal", pointKey: "goalPoints" },
    { label: "Goals Conceded", calcKey: "goalsConceded", pointKey: "goalsConcededPoints" },
    { label: "Goal Assists", calcKey: "goalAssists", pointKey: "goalAssistsPoints" },
    { label: "Goals Saved", calcKey: "goalSaves", pointKey: "goalSavesPoints" },
    { label: "Ball Passes", calcKey: "passes", pointKey: "passesPoints" },
    { label: "Fouls", calcKey: "fouls", pointKey: "foulsPoints" },
    { label: "Yellow Cards", calcKey: "yellowCard", pointKey: "yellowCardPoints" },
    { label: "Red Cards", calcKey: "redCard", pointKey: "redCardPoints" },
    { label: "Tackle Blocks", calcKey: "tackleBlock", pointKey: "tackleBlockPoints" },
    { label: "Tackle Interceptions", calcKey: "tackleInterception", pointKey: "tackleInterceptionPoints" },
    { label: "Duels", calcKey: "duel", pointKey: "duelPoints" },
    { label: "Dribbles", calcKey: "dribble", pointKey: "dribblePoints" },
];

const STAT_ROWS_FR = [
    { label: "Minutes jouées", calcKey: "minutes", pointKey: "minutesPoints" },
    { label: "Note du joueur", calcKey: "rating", pointKey: "ratingPoints" },
    { label: "Tirs au but", calcKey: "shots", pointKey: "shotsPoints" },
    { label: "Objectifs", calcKey: "goal", pointKey: "goalPoints" },
    { label: "Buts encaissés", calcKey: "goalsConceded", pointKey: "goalsConcededPoints" },
    { label: "Aides aux buts", calcKey: "goalAssists", pointKey: "goalAssistsPoints" },
    { label: "Buts enregistrés", calcKey: "goalSaves", pointKey: "goalSavesPoints" },
    { label: "Passes de balle", calcKey: "passes", pointKey: "passesPoints" },
    { label: "Fautes", calcKey: "fouls", pointKey: "foulsPoints" },
    { label: "Cartons jaunes", calcKey: "yellowCard", pointKey: "yellowCardPoints" },
    { label: "Cartons rouges", calcKey: "redCard", pointKey: "redCardPoints" },
    { label: "S'attaquer aux blocs", calcKey: "tackleBlock", pointKey: "tackleBlockPoints" },
    { label: "Interceptions de plaquage", calcKey: "tackleInterception", pointKey: "tackleInterceptionPoints" },
    { label: "Duels", calcKey: "duel", pointKey: "duelPoints" },
    { label: "Dribbles", calcKey: "dribble", pointKey: "dribblePoints" },
];

const PlayerPointsModal2 = ({ matchFixtureId, playerId, playerPointsModal }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [player, setPlayer] = useState({});
    const [points, setPoints] = useState({});
    const [totalPoints, setTotalPoints] = useState({});
    const [calculations, setCalculations] = useState({});

    const { userDetails } = useSelector(state => state?.persisted?.userSlice);
    const { t } = useTranslation();
    const { selectedLanguage } = useSelector((state) => state.languageSlice);
    const dispatch = useDispatch();

    const fetchDataFromBackend = async () => {
        try {
            setLoading(true);
            const response = await getPlayerPointsApi(matchFixtureId, playerId, userDetails?.token);
            if (response?.data?.status == 0) {
                setError(true);
            } else {
                setError(false);
                setPlayer(response?.data?.player);
                setPoints(response?.data?.points);
                setTotalPoints(response?.data?.totalPoints);
                setCalculations(response?.data?.calculations);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toast.error(t("20"));
        }
    };

    useEffect(() => {
        if (playerPointsModal || playerId) {
            fetchDataFromBackend();
        }
    }, [playerPointsModal, playerId]);

    const handleClose = () => dispatch(togglePlayerPointsModal({ matchFixtureId: null, playerId: null }));

    return (
        <Dialog
            header={t("219")}
            visible={playerPointsModal}
            position="bottom"
            style={{ width: "100%", maxWidth: "480px", margin: 0 }}
            onHide={handleClose}
        >
            {loading ? (
                <div style={{ padding: "40px", display: "flex", justifyContent: "center" }}>
                    <SmallLoader />
                </div>
            ) : !error ? (
                <div className={classes.container}>
                    {/* Player header */}
                    <div className={classes.panel_header}>
                        <div className={classes.image_container}>
                            <img src={player?.playerPhoto} alt={player?.playerName} className={classes.image} />
                        </div>
                        <span className={classes.text}>{player?.playerName}</span>
                    </div>

                    {/* Stat rows */}
                    <div className={classes.panel_container}>
                        {selectedLanguage == 0 ? STAT_ROWS.map(({ label, calcKey, pointKey }) => (
                            <div className={classes.grid} key={pointKey}>
                                <span className={classes.text}>
                                    {label}
                                    {calculations?.[calcKey] !== undefined && (
                                        <> ({calculations[calcKey]})</>
                                    )}
                                </span>
                                <span className={classes.text_right}>{points?.[pointKey] ?? 0}</span>
                            </div>
                        )) :
                            STAT_ROWS_FR.map(({ label, calcKey, pointKey }) => (
                                <div className={classes.grid} key={pointKey}>
                                    <span className={classes.text}>
                                        {label}
                                        {calculations?.[calcKey] !== undefined && (
                                            <> ({calculations[calcKey]})</>
                                        )}
                                    </span>
                                    <span className={classes.text_right}>{points?.[pointKey] ?? 0}</span>
                                </div>
                            ))
                        }
                    </div>

                    {/* Total points */}
                    <div className={classes.btn_container}>
                        <div className={classes.points_banner}>
                            <p className={classes.points_label}>{t("220")}</p>
                            <p className={classes.points_value}>{totalPoints?.totalPoints ?? 0}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className={classes.message_box}>
                    <p className={classes.message}>{t("221")}</p>
                </div>
            )}
        </Dialog>
    );
};

export default PlayerPointsModal2;